import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Newspaper } from 'lucide-react';
import { listenPublishedNews } from '../../../database/news';
import SEO from '../../SEO';
import { absoluteUrl } from '../../../lib/seo';

const fmt = (ts) => {
  if (!ts) return '';
  const date = ts?.toDate ? ts.toDate() : new Date(ts);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
};

const excerpt = (blocks, max = 180) => {
  const text = blocks.find((b) => b.type === 'text')?.html || '';
  const plain = text
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (plain.length <= max) return plain;
  return `${plain.slice(0, max).trimEnd()}…`;
};

const NewsIndex = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = listenPublishedNews((data) => {
      setItems(data);
      setLoading(false);
    });
    return unsub;
  }, []);

  const blogLd = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'KZCOMP News',
    url: absoluteUrl('/news'),
    description:
      'Tournament announcements, map drops, and scene reports for the competitive KZ community.',
    blogPost: items.slice(0, 20).map((p) => ({
      '@type': 'BlogPosting',
      headline: p.title,
      url: absoluteUrl(`/news/${p.slug || p.id}`),
    })),
  };

  return (
    <div className="mx-auto w-full max-w-screen-lg px-4 pb-24 pt-12 sm:px-6">
      <SEO
        title="News — Tournament Reports &amp; Map Drops"
        description="Tournament announcements, map drops, and scene reports for the competitive KZ community."
        canonicalPath="/news"
        jsonLd={blogLd}
      />
      <div className="mb-10 flex flex-col gap-3">
        <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
          <span className="h-px w-6 bg-heat" />
          Briefings
        </div>
        <h1 className="font-display text-5xl font-bold tracking-tight sm:text-6xl">News</h1>
        <p className="max-w-xl text-sm text-muted-foreground sm:text-base">
          Tournament announcements, map drops, and scene reports.
        </p>
      </div>

      {loading ? (
        <div className="space-y-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-32 animate-pulse rounded-md border border-border/60 bg-muted/10"
            />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-border/60 px-6 py-20 text-center">
          <Newspaper className="mx-auto mb-4 h-8 w-8 text-muted-foreground/50" />
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            No news yet — check back soon.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-border/60 border-y border-border/60">
          {items.map((post) => (
            <li key={post.id}>
              <Link
                to={`/news/${post.slug}`}
                className="group flex items-center gap-6 px-2 py-6 transition-colors hover:bg-secondary/30"
              >
                {post.coverImage?.url && (
                  <img
                    src={post.coverImage.url}
                    alt=""
                    className="hidden h-24 w-32 flex-shrink-0 rounded-md border border-border/60 object-cover sm:block"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                    {fmt(post.publishedAt)}
                  </div>
                  <h2 className="font-display text-2xl font-semibold leading-tight transition-colors group-hover:text-heat">
                    {post.title || 'Untitled'}
                  </h2>
                  <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
                    {excerpt(post.blocks || [])}
                  </p>
                </div>
                <ChevronRight className="hidden h-5 w-5 flex-shrink-0 text-muted-foreground transition-colors group-hover:text-heat sm:block" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default NewsIndex;
